import { useState, useEffect, useRef, useCallback } from 'react';
import FullLayout from 'layout/FullLayout';
import HomeFeatures from './HomeFeatures';

const Home = () => {
  /**
   * step state machine:
   *  'hero'         → hero fully visible, body scroll locked
   *  'to-features'  → hero fading out (500 ms)
   *  'features-in'  → features mounted at opacity-0, ready to fade in
   *  'features'     → features fully visible, body scroll free
   *  'to-hero'      → features fading out (500 ms), then back to 'hero'
   */
  // ── State & refs ──────────────────────────────────────────────────────────────
  const [step, setStep] = useState('hero'); // 'hero' | 'to-features' | 'features-in' | 'features' | 'to-hero'
  const [featureSection, setFeatureSection] = useState(0); // 0 = KiNSTALLATION | 1 = KiNTERACTIVE
  const locked = useRef(false); // single lock — blocks ALL navigation during any transition
  const touchStartY = useRef(0);

  // ── Navigators ────────────────────────────────────────────────────────────────
  const goToFeatures = useCallback(() => {
    if (locked.current || step !== 'hero') return;
    locked.current = true;
    setStep('to-features');
    setTimeout(() => {
      window.scrollTo(0, 0);
      setStep('features-in');
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          setStep('features');
          setTimeout(() => {
            locked.current = false;
          }, 700);
        }),
      );
    }, 500);
  }, [step]);

  const goToHero = useCallback(() => {
    if (locked.current || step !== 'features') return;
    locked.current = true;
    window.scrollTo(0, 0);
    setStep('to-hero');
    setFeatureSection(0);
    setTimeout(() => {
      setStep('hero');
      setTimeout(() => {
        locked.current = false;
      }, 700);
    }, 500);
  }, [step]);

  const goToSection = useCallback(
    (idx) => {
      if (locked.current || step !== 'features') return;
      locked.current = true;
      setFeatureSection(idx);
      setTimeout(() => {
        locked.current = false;
      }, 600);
    },
    [step],
  );

  // ── Body scroll lock ──────────────────────────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // ── Scroll / swipe hijack ─────────────────────────────────────────────────────
  useEffect(() => {
    const handleNav = (dy) => {
      if (step === 'hero' && dy > 0) goToFeatures();
      else if (step === 'features') {
        if (dy > 0 && featureSection === 0) goToSection(1);
        else if (dy < 0 && featureSection === 1) goToSection(0);
        else if (dy < 0 && featureSection === 0) goToHero();
      }
    };

    const onWheel = (e) => {
      if (step === 'hero' || step === 'features') e.preventDefault();
      if (Math.abs(e.deltaY) > 30) handleNav(e.deltaY);
    };

    const onTouchStart = (e) => {
      touchStartY.current = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      const dy = touchStartY.current - e.changedTouches[0].clientY;
      if (Math.abs(dy) > 50) handleNav(dy);
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [step, featureSection, goToFeatures, goToHero, goToSection]);

  const showHero = step === 'hero' || step === 'to-features';
  const showFeatures = step === 'features-in' || step === 'features' || step === 'to-hero';
  // true while hero is animating out on scroll-down
  const isExiting = step === 'to-features';

  return (
    <FullLayout>
      {/* ── Hero ── */}
      {showHero && (
        <div
          className="w-full relative flex justify-center items-center"
          style={{ height: 'calc(100vh - 60px)' }}>
          {/* Bird — slides down on enter, slides up on exit */}
          <div className="absolute top-0 xl:left-[90px] lg:left-[10px] md:left-0">
            <div className="w-full h-full overflow-hidden">
              <img
                className={`xl:h-[220px] lg:h-[170px] md:h-[180px] w-auto animate__animated ${
                  isExiting
                    ? 'animate__slideOutUp animate__faster'
                    : 'animate__slideInDown animate__slow'
                }`}
                src="kin/assest/bird.png"
                alt=""
              />
            </div>
          </div>

          {/* Ranting — fades in from right on enter, slides out right on exit */}
          <div className="absolute top-0 right-0 hidden md:block">
            <div className="w-full h-full relative">
              <img
                className={`xl:h-[170px] lg:h-[140px] md:h-[110px] w-auto animate__animated ${
                  isExiting
                    ? 'animate__slideOutRight animate__faster'
                    : 'animate__fadeInRight animate__slow'
                }`}
                src="kin/assest/ranting.png"
                alt=""
              />
              {/* Orang-utan — shakes in on enter, slides out right on exit */}
              <div className="absolute bottom-0 xl:top-[145px] xl:right-[100px] lg:top-[130px] lg:right-[90px] md:top-[100px] md:right-[80px]">
                <img
                  className={`xl:h-[350px] lg:h-[250px] md:h-[180px] w-auto animate__animated ${
                    isExiting
                      ? 'animate__slideOutRight animate__faster'
                      : 'animate__shakeY animate__slow'
                  }`}
                  src="kin/assest/orang-utan.png"
                  alt=""
                />
              </div>
            </div>
          </div>

          {/* Center text — fades up on enter, fades up on exit */}
          <div className="flex flex-col justify-center text-center gap-3">
            <h1
              className={`font-bold text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-gradient-animate max-w-xs sm:max-w-sm md:max-w-lg lg:max-w-2xl xl:max-w-3xl leading-tight tracking-tight animate__animated ${
                isExiting ? 'animate__fadeOutUp animate__faster' : 'animate__fadeInUp'
              }`}>
              A creative space to learn, connect and experience culture, inspired by Nusantara
            </h1>
            <button
              onClick={(e) => {
                e.preventDefault();
                goToFeatures();
              }}
              className={`mt-3 md:py-1.5 py-2 px-3.5 bg-primary-main text-white rounded-3xl text-sm mx-auto animate__animated ${
                isExiting ? 'animate__fadeOutUp animate__faster' : 'animate__fadeInUp'
              }`}>
              LEARN MORE
            </button>
          </div>

          {/* Komodo — light-speed in from left on enter, light-speed out left on exit */}
          <div className="absolute bottom-0 left-0">
            <div className="w-full h-full overflow-hidden">
              <img
                className={`xl:h-[400px] lg:h-[300px] md:h-[330px] h-[280px] w-auto animate__animated ${
                  isExiting
                    ? 'animate__lightSpeedOutLeft animate__faster'
                    : 'animate__lightSpeedInLeft animate__slow'
                }`}
                src="kin/assest/komodo.png"
                alt=""
              />
            </div>
          </div>

          {/* Fish3 (md only) — fades in from right on enter, fades out right on exit */}
          <div className="absolute md:block lg:hidden hidden right-0 bottom-[20px]">
            <div className="w-full h-full overflow-hidden">
              <img
                className={`h-[270px] w-auto animate__animated ${
                  isExiting
                    ? 'animate__fadeOutRight animate__faster'
                    : 'animate__fadeInRight animate__slow'
                }`}
                src="kin/assest/fish3.png"
                alt=""
              />
            </div>
          </div>

          {/* Fish (lg only) — fades up on enter, fades down on exit */}
          <div className="absolute bottom-0 xl:right-[350px] lg:right-[80px] lg:block md:hidden hidden">
            <div className="w-full h-full overflow-hidden">
              <img
                className={`xl:h-[210px] lg:h-[140px] md:h-[180px] w-auto animate__animated ${
                  isExiting
                    ? 'animate__fadeOutDown animate__faster'
                    : 'animate__fadeInUp animate__slow'
                }`}
                src="kin/assest/fish.png"
                alt=""
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Features ── */}
      {showFeatures && (
        <div
          className={`transition-all duration-700 ease-out ${
            step === 'features'
              ? 'opacity-100 translate-y-0'
              : step === 'features-in'
              ? 'opacity-0 translate-y-5'
              : 'opacity-0 -translate-y-2 pointer-events-none'
          }`}>
          <HomeFeatures activeSection={featureSection} onSectionChange={goToSection} />
        </div>
      )}
    </FullLayout>
  );
};

export default Home;
